import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import SwitchField from '@components/Form/SwitchField';
import TextField from '@components/Form/TextField';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { ACCESS_PRO } from '@src/constants';
import { ClickTarget, ContentType, click } from '@tracking';
import { isGroupAdmin, isGroupUser } from '@utils/corporateSubscriptionHelpers';

import {
  FormButtonType,
  FormValues,
  GroupAdmin,
  GroupUser,
  UserOperationType,
  UserType,
} from '../../types';
import { useUserForm } from './useUserForm';
import validationSchema from './validationSchema';

interface UserFormProps {
  user: GroupAdmin | GroupUser;
  operation: UserOperationType;
  onClose: () => void;
}

const RESEND_EMAIL_INVITATION_TEXT = 'Resend email invitation';

const UserForm = ({ user, operation, onClose }: UserFormProps) => {
  const { email, name, type } = user;
  const {
    currentUser: { id: currentUserId },
  } = useCurrentUser();
  const isCurrentAdmin = currentUserId === user.id && type === UserType.Admin;
  const { getFormHeadline, getButtonText, handleSubmit, sendGroupAdminWelcomeEmail } = useUserForm(
    type,
    operation
  );
  const initialValues: FormValues = {
    email,
    name,
    pro: isGroupUser(user) ? user.accessLevel === ACCESS_PRO : false,
  };
  const headline = getFormHeadline();
  const primaryButtonText = getButtonText(FormButtonType.PRIMARY);
  const secondaryButtonText = getButtonText(FormButtonType.SECONDARY);
  const { trackEvent } = useEventTracking();

  const handleFormSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      const sanitizedValues = {
        ...values,
        name: values.name.trim(),
        email: values.email.trim(),
      };
      await handleSubmit(sanitizedValues, user, FormButtonType.PRIMARY);
      trackEvent(
        click({
          clickTarget: ClickTarget.Button,
          clickText: primaryButtonText,
          contentId: user.id,
          contentType: ContentType.User,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  const handleSecondaryButtonClick = async (values: FormValues) => {
    try {
      const sanitizedValues = {
        ...values,
        name: values.name.trim(),
        email: values.email.trim(),
      };
      await handleSubmit(sanitizedValues, user, FormButtonType.SECONDARY);
      trackEvent(
        click({
          clickTarget: ClickTarget.Button,
          clickText: secondaryButtonText,
          contentId: user.id,
          contentType: ContentType.User,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  const handleWelcomeEmail = async () => {
    if (!isGroupAdmin(user)) {
      return;
    }

    try {
      await sendGroupAdminWelcomeEmail({
        user_ids: user.id,
        organization_id: user.organizationId,
      });
      trackEvent(
        click({
          clickTarget: ClickTarget.Link,
          clickText: RESEND_EMAIL_INVITATION_TEXT,
          contentId: user.id,
          contentType: ContentType.User,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <div className="flex flex-col gap-16">
            {headline && (
              <h4 className="m-0 font-suisse-intl-mono text-caption-md text-primary-magenta">
                {headline}
              </h4>
            )}
            <TextField
              inputClassName="bg-transparent border-grayscale-gray text-black disabled:!bg-transparent"
              name="name"
              placeholder="Full name"
              disabled={type === UserType.Archived}
            />
            <TextField
              inputClassName="bg-transparent border-grayscale-gray text-black disabled:!bg-transparent"
              name="email"
              placeholder="Email"
              disabled={type === UserType.Archived}
            />
            {isGroupUser(user) && (
              <SwitchField
                name="pro"
                colorTheme={Theme.Gold}
                width={51}
                height={30}
                label="Pro account"
                switchClassName="!mr-16 !rounded-[16px]"
                labelClassName="!text-button-md"
              />
            )}
            <PrimaryButton
              type="submit"
              as="button"
              size="small"
              className="w-full"
              disabled={isSubmitting}
            >
              {primaryButtonText}
            </PrimaryButton>
            {secondaryButtonText && !isCurrentAdmin && (
              <PrimaryButton
                type="button"
                as="button"
                size="small"
                className="w-full !border-grayscale-gray !bg-grayscale-gray hover:!border-grayscale-beige hover:!bg-grayscale-beige hover:!text-grayscale-gray"
                onClick={() => handleSecondaryButtonClick(values)}
                disabled={isSubmitting}
              >
                {secondaryButtonText}
              </PrimaryButton>
            )}
            {!isCurrentAdmin && type === UserType.Admin && (
              <TextButton
                type="button"
                as="button"
                onClick={handleWelcomeEmail}
                className="font-suisse-intl text-button-md text-black hover:text-primary-magenta"
              >
                {RESEND_EMAIL_INVITATION_TEXT}
              </TextButton>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default UserForm;
